<template>
	<div>
		<!-- En-tête : titre + bouton de création -->
		<div class="d-flex align-items-center my-2">
			<h2 class="mb-0 mr-auto text-truncate">
				<font-awesome-icon :icon="['cf', 'pax']" /> {{ $t('pax.simulations_croisement_de') }} {{ horse.horse_nom }}
			</h2>
			<b-button @click="openSimulationModal" variant="primary" size="sm" class="ml-2 flex-shrink-0">
				{{ $t('pax.nouvelle_simulation') }}
				<font-awesome-icon :icon="['fal', 'plus']" />
			</b-button>
		</div>

		<LoadingSpinner v-if="table_busy" />

		<div class="mt-2" v-else>
			<EmptyState v-if="simulations.length == 0" :label="$t('pax.aucune_simulation')">
				<template #action>
					<b-button @click="openSimulationModal" variant="primary" size="sm">
						{{ $t('pax.nouvelle_simulation') }}
						<font-awesome-icon :icon="['fal', 'plus']" />
					</b-button>
				</template>
			</EmptyState>

			<template v-else>
				<!-- Vue cards : mobile uniquement -->
				<div class="d-md-none">
					<div
						v-for="simulation in simulations"
						:key="'card-' + simulation.simulation_id"
						class="border-bottom py-3 cursor-pointer"
						@click="printSimulation(simulation)"
					>
						<div class="d-flex align-items-start min-w-0">
							<div class="flex-fill min-w-0 overflow-hidden">
								<div class="d-flex align-items-center mb-1 min-w-0">
									<span class="pax-grade-tile text-truncate" :class="getGlobalColor(simulation.color_grade, simulation.genetic_indicator_color)">{{ simulation.stallion_nom }}</span>
									<span class="pax-grade-tile icon ml-1" :class="simulation.genetic_indicator_color" :title="$t('pax.compatibilite_genetique')"><font-awesome-icon :icon="['fal', 'venus-mars']" /></span>
									<span class="pax-grade-tile icon ml-1" :class="simulation.color_grade" :title="$t('pax.compatibilite_phenotypique')"><font-awesome-icon :icon="['cf', 'puzzle']" /></span>
								</div>
								<small class="text-muted">{{ formatDate(simulation.simulation_date) }}</small>
							</div>
							<font-awesome-icon v-if="processing_id === simulation.simulation_id" :icon="['fas', 'spinner']" pulse class="text-muted ml-2 flex-shrink-0 mt-1" />
							<font-awesome-icon v-else :icon="['fal', 'download']" class="text-muted ml-2 flex-shrink-0 mt-1" />
							<b-button variant="link" size="sm" class="p-0 ml-3 flex-shrink-0 text-muted" :title="$t('global.supprimer')" @click.stop="askDeleteSimulation(simulation)">
								<font-awesome-icon :icon="['fal', 'trash-alt']" />
							</b-button>
						</div>
					</div>
				</div>

				<!-- Vue tableau : desktop uniquement -->
				<div class="d-none d-md-block table-responsive">
					<table class="table table-hover mb-0">
						<thead class="small text-muted">
							<tr>
								<th>{{ $t('pax.stallion') }}</th>
								<th class="text-nowrap">{{ $t('monte.date') }}</th>
								<th class="text-nowrap text-center">{{ $t('pax.compatibilite_genetique') }}</th>
								<th class="text-nowrap text-center">{{ $t('pax.compatibilite_phenotypique') }}</th>
								<th class="text-right">{{ $t('monte.action') }}</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="simulation in simulations" :key="simulation.simulation_id">
								<td class="small" style="max-width:250px">
									<span class="pax-grade-tile text-truncate mw-100" :class="getGlobalColor(simulation.color_grade, simulation.genetic_indicator_color)">{{ simulation.stallion_nom }}</span>
								</td>
								<td class="small text-nowrap">{{ formatDate(simulation.simulation_date) }}</td>
								<td class="text-center"><span class="pax-grade-tile icon" :class="simulation.genetic_indicator_color"><font-awesome-icon :icon="['fal', 'venus-mars']" /></span></td>
								<td class="text-center"><span class="pax-grade-tile icon" :class="simulation.color_grade"><font-awesome-icon :icon="['cf', 'puzzle']" /></span></td>
								<td class="text-right text-nowrap">
									<b-button variant="link" size="sm" class="p-0" @click="printSimulation(simulation)">
										{{ $t('pax.telecharger') }}
										<font-awesome-icon v-if="processing_id === simulation.simulation_id" :icon="['fas', 'spinner']" pulse />
										<font-awesome-icon v-else :icon="['fal', 'download']" />
									</b-button>
									<b-button variant="link" size="sm" class="p-0 ml-3 text-muted" :title="$t('global.supprimer')" @click="askDeleteSimulation(simulation)">
										<font-awesome-icon :icon="['fal', 'trash-alt']" />
									</b-button>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</template>
		</div>

		<SimulationModal ref="simulation_modal" :mare="horse" @submit="loadSimulations" />

		<b-modal ref="confirm_delete" hide-footer centered @hidden="simulation_to_delete = null">
			<template v-slot:modal-title>{{ $t('pax.confirm_supprimer_simulation') }}</template>

			<p v-if="simulation_to_delete" class="mb-3">
				{{ $t('pax.texte_supprimer_simulation', { mare_nom: horse.horse_nom, stallion_nom: simulation_to_delete.stallion_nom }) }}
			</p>

			<div class="d-flex justify-content-end">
				<b-button variant="secondary" class="mr-2" :disabled="deleting" @click="$refs.confirm_delete.hide()">{{ $t('global.annuler') }}</b-button>
				<b-button variant="primary" :disabled="deleting" @click="confirmDeleteSimulation">
					<b-spinner v-if="deleting" small />
					<template v-else>{{ $t('global.supprimer') }} <font-awesome-icon :icon="['fal', 'trash-alt']" /></template>
				</b-button>
			</div>
		</b-modal>
	</div>
</template>


<script type="text/javascript">
	import PaxMixin from '@/mixins/Pax.js'

	export default {
		name: "PaxSimulationList",
		mixins: [PaxMixin],
		props: ['horse'],
		data() {
			return {
				simulations: [],
				table_busy: true,
				processing_id: null,
				simulation_to_delete: null,
				deleting: false
			}
		},
		// la fiche cheval garde ses onglets en keep-alive : l'historique est relu à chaque retour sur
		// l'onglet, la caractérisation de la jument ayant pu changer entre-temps, et avec elle les grades
		activated() {
			this.loadSimulations()
		},
		methods: {
			/**
			 * Charge l'historique des simulations de croisement de la jument.
			 */
			async loadSimulations() {
				this.table_busy = true

				const simulations = await this.getMareSimulations(this.horse.horse_id)
				// getMareSimulations renvoie null lorsque l'appel échoue
				this.simulations = simulations !== null ? simulations : []

				this.table_busy = false
			},

			openSimulationModal() {
				this.$refs.simulation_modal.open()
			},

			askDeleteSimulation(simulation) {
				this.simulation_to_delete = simulation
				this.$refs.confirm_delete.show()
			},

			/**
			 * Retire la simulation confirmée de l'historique, puis recharge la liste.
			 */
			async confirmDeleteSimulation() {
				this.deleting = true
				try {
					await this.deleteMareSimulation(this.horse.horse_id, this.simulation_to_delete.simulation_id)
					this.$refs.confirm_delete.hide()
					await this.loadSimulations()
				}
				finally {
					// en cas d'échec l'erreur est déjà signalée par la requête : la modale reste ouverte
					this.deleting = false
				}
			},

			/**
			 * Rejoue la simulation d'une ligne pour retélécharger son PDF de résultats, recalculé
			 * avec les valeurs actuelles du client ; la liste est rechargée pour en refléter la date.
			 *
			 * @param {Object} simulation ligne de l'historique
			 */
			async printSimulation(simulation) {
				this.processing_id = simulation.simulation_id
				try {
					await this.printSimulationCroisement(this.horse, { horse_id: simulation.stallion_id, horse_nom: simulation.stallion_nom })
					await this.loadSimulations()
				}
				finally {
					// l'API rejette si la jument n'appartient pas au client : le spinner doit s'arrêter
					this.processing_id = null
				}
			}
		},

		components: {
			LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35'),
			EmptyState: () => import('@/components/EmptyState'),
			SimulationModal: () => import('@/components/Pax/SimulationModal')
		}
	}
</script>
